import { DEFAULT_PAGE_SIZE } from '@/constants';
import { PaymentAccountType, WithdrawalMode } from '@/constants/merchant';
import { DataStatus, ListResponse } from '@/types/common';
import { ResourcePositionInfo } from '@/types/marketing';
import {
  BalanceDetail,
  WxHomeSettingsData,
  MerchantBalanceFlowDetail,
  MerchantBalanceFlowListParams,
  MerchantInitStatus,
  MerchantOssUsage,
  OssCostDetail,
  PaymentAccount,
  PaymentAccountBalance,
  WithdrawalRecord,
  SaveWxHomeConfigParams,
  ValueAddedServiceInfo,
} from '@/types/merchant';
import { ContractTemplate, MerchantSettings, MiniProgramConfig } from '@/types/merchant';
import { requestWrapper } from '@/utils/request';

// 获取商户小程序配置
export async function getWxConfig() {
  return requestWrapper<MiniProgramConfig>('/api/merchant/wx_config', {
    method: 'GET',
  });
}

// 创建商户小程序配置
export async function addWxConfig(body: Partial<MiniProgramConfig>) {
  return requestWrapper('/api/merchant/wx_config/create', {
    method: 'POST',
    data: body,
  });
}

// 更新商户小程序配置
export async function updateWxConfig(body: Partial<MiniProgramConfig>) {
  return requestWrapper('/api/merchant/wx_config/update', {
    method: 'POST',
    data: body,
  });
}

// 获取商户配置
export async function getMerchantConfig() {
  return requestWrapper<MerchantSettings>('/api/merchant/config', {
    method: 'GET',
    showError: false,
  });
}

// 增加商户配置
export async function addMerchantConfig(body: Partial<MerchantSettings>) {
  return requestWrapper<any>('/api/merchant/config/create', {
    method: 'POST',
    data: body,
  });
}

// 更新商户配置
export async function updateMerchantConfig(body: Partial<MerchantSettings>) {
  return requestWrapper<any>('/api/merchant/config/update', {
    method: 'POST',
    data: body,
  });
}

// 获取合同列表
export async function getContractList(params?: {
  page: number;
  page_size: number;
  status?: number;
}) {
  const defaultParams = {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    status: DataStatus.Active, // 默认只查询正常状态的合同
  };

  const mergedParams = {
    ...defaultParams,
    ...params,
  };
  return requestWrapper<ListResponse<ContractTemplate>>('/api/contract_template/list', {
    method: 'GET',
    params: mergedParams,
  });
}

// 创建合同模板
export async function createContractTemplate(params: {
  title: string;
  content: string;
  company_name: string;
  company_seal_url: string;
}) {
  return requestWrapper<{ success: boolean }>('/api/contract_template/create', {
    method: 'POST',
    data: params,
  });
}

// 编辑合同模板
export async function editContractTemplate(params: {
  id: number;
  title: string;
  content: string;
  company_name: string;
  company_seal_url: string;
  status: number;
}) {
  return requestWrapper<{ success: boolean }>('/api/contract_template/update', {
    method: 'POST',
    data: params,
  });
}

// 获取收款账户列表
export async function getPaymentAccountList() {
  return requestWrapper<ListResponse<PaymentAccount>>('/api/payment_config/list', {
    method: 'GET',
    params: {},
  });
}

// 收款账户获取验证码
export async function getPaymentAccountVerifyCode(params: {
  id: number;
  phone: string;
  phone_type?: string; // 固定传1，绑定手机类型：1-法人手机号 2-企业授权手机号
}) {
  const mergedParams = {
    ...params,
    phone_type: params.phone_type || '1',
  };
  return requestWrapper<{ resp_trace_num: string }>('/api/payment/member_bind_phone', {
    method: 'POST',
    data: mergedParams,
  });
}

// 收款账户绑定手机号
export async function confirmBindPaymentAccountPhone(params: {
  id: number;
  phone: string;
  verify_code: string; // 验证码
  apply_resp_trace_num: string; // 通联返回的响应业务关联流水号
}) {
  return requestWrapper('/api/payment/confirm_bind_phone', {
    method: 'POST',
    data: params,
  });
}

// 刷新签约地址
export async function getSignUrl(params: {
  payment_config_id: number; // 收款账户ID
  agreement_type: string; // 固定传'3'
  agreement_info: {
    coupon_way: string; // 固定传 '1', 按比例分账
    coupon_rate_int: number; // 平台分成比例，千分之x
  };
}) {
  return requestWrapper<{ sign_agreement_url: string }>('/api/payment/online_agreement_sign', {
    method: 'POST',
    data: params,
  });
}

// 刷新微信/支付宝认证地址
export async function getAtAuthUrl(params: {
  payment_config_id: number; // 收款账户ID
  page_type: 'wx' | 'ali';
}) {
  return requestWrapper<{ url: string }>('/api/payment/at_unified_auth', {
    method: 'POST',
    data: params,
  });
}

// 门店绑定收款账户
export async function bindStudioPaymentAccount(params: {
  store_id: number; // 门店ID
  payment_config_id: number; // 收款账户ID
}) {
  return requestWrapper('/api/payment/bind_store', {
    method: 'POST',
    data: params,
  });
}

// 获取收款账户余额
export async function getPaymentAccountBalance(params?: {
  acct_type: PaymentAccountType; // 账户类型
}) {
  const acctType = params?.acct_type || PaymentAccountType.PendingSettlement;
  const mergedParams = {
    ...params,
    acct_type: acctType,
  };
  return requestWrapper<ListResponse<PaymentAccountBalance>>('/api/payment/account_balance_query', {
    method: 'GET',
    params: mergedParams,
  }).then((res) => {
    return {
      ...res,
      list: (res.list || []).map((item) => {
        const balance =
          (item.balance_detail || []).find((item) => item.acct_type === acctType) ||
          ({} as BalanceDetail);
        return {
          ...item,
          balance,
        };
      }),
    };
  });
}

// 提现操作
export async function withdrawMoney(params: {
  payment_config_id: number; // 收款账户ID
  order_amount: number; // 提现金额，单位分
  remark?: string; // 备注
}) {
  return requestWrapper('/api/payment/withdraw_application', {
    method: 'POST',
    data: params,
  });
}

// 提现设置
export async function withdrawSetting(params: {
  payment_config_id: number; // 收款账户ID
  auto: WithdrawalMode; // 1自动提现 0手动
  refund_reserved_amount?: number; // 账户预留金额，单位分（仅自动提现时传递）
}) {
  return requestWrapper('/api/payment/withdraw_setting', {
    method: 'POST',
    data: params,
  });
}

// 获取提现记录
export async function getWithdrawRecord(
  params: {
    page?: number;
    page_size?: number;
    payment_config_id?: number; // 收款账户ID
    start_time?: string; // 开始时间
    end_time?: string; // 结束时间
  } = {},
) {
  return requestWrapper<ListResponse<WithdrawalRecord>>('/api/payment/withdraw_list', {
    method: 'GET',
    params: params,
  });
}

// 添加oss媒体记录(统计上传视频流量)
export async function addOssMediaRecord(params: {
  media_size_kb: number; // 媒体大小 KB
  media_type: number; // 媒体类型 1: 课堂视频 2: 课程预告
  media_url: string; // 媒体URL
  operation_type: number; // 操作类型 1: 上传
  relate_type: number; // 关联类型 1: 课程
  relate_id: number; // 关联课程ID
  remark?: string; // 备注
}) {
  return requestWrapper('/api/oss_media_record/create', {
    method: 'POST',
    data: params,
  });
}

// 获取商户oss余额
export async function getOssBalance() {
  return requestWrapper<MerchantOssUsage>('/api/merchant_oss/balance', {
    method: 'GET',
    params: {},
  });
}

// 获取商户增值服务信息
export async function getValueAddedService() {
  return requestWrapper<ValueAddedServiceInfo>('/api/merchant/value_added_service', {
    method: 'GET',
  });
}

// 获取oss明细列表
export async function geOssCostList(params: { page: number; page_size: number }) {
  return requestWrapper<ListResponse<OssCostDetail>>('/api/oss_daily_cost_summary/list', {
    method: 'GET',
    params,
  });
}

// 获取共用余额流水列表
export async function getMerchantBalanceFlowList(params: MerchantBalanceFlowListParams) {
  return requestWrapper<ListResponse<MerchantBalanceFlowDetail>>(
    '/api/merchant_balance_flow/list',
    {
      method: 'GET',
      params,
    },
  );
}

// 初始化引导状态
export async function getMerchantInitStatus() {
  return requestWrapper<MerchantInitStatus>('/api/merchant/init_guide_status', {
    method: 'GET',
  }).then((res) => {
    return {
      ...res,
      hasInitializedAll: Object.values(res).every((item) => item === true),
    };
  });
}

// 获取小程序首页配置
export async function getWxHomeConfig() {
  return requestWrapper<WxHomeSettingsData>('/api/merchant/wx_home/config', {
    method: 'GET',
  });
}

// 保存小程序首页配置
export async function saveWxHomeConfig(params: Partial<SaveWxHomeConfigParams>) {
  return requestWrapper('/api/merchant/wx_home/config/save', {
    method: 'POST',
    data: params,
  });
}

// 增加作品
export async function addWork(params: {
  title: string;
  video_url: string;
  cover_url?: string;
  video_duration?: number; // 视频时长秒
  video_size: number; // 媒体大小 KB
}) {
  return requestWrapper('/api/work_videos/create', {
    method: 'POST',
    data: params,
  });
}

// 删除商户作品视频
export async function deleteWork(video_id: number) {
  return requestWrapper('/api/work_videos/delete', {
    method: 'POST',
    data: {
      video_id,
    },
  });
}

// 编辑作品
export async function updateWork(params: { id: number; title?: string; cover_url?: string }) {
  return requestWrapper('/api/work_videos/update', {
    method: 'POST',
    data: params,
  });
}

// 近期活动海报列表
export async function getActivityPosterList() {
  return requestWrapper<ListResponse<ResourcePositionInfo>>('/api/merchant/wx_home/poster/list', {
    method: 'GET',
  });
}

// 创建近期活动海报
export async function createActivityPoster(params: Partial<ResourcePositionInfo>) {
  return requestWrapper<void>('/api/merchant/wx_home/poster/create', {
    method: 'POST',
    data: params,
  });
}

// 编辑近期活动海报
export async function updateActivityPoster(params: Partial<ResourcePositionInfo>) {
  return requestWrapper<void>('/api/merchant/wx_home/poster/update', {
    method: 'POST',
    data: params,
  });
}

// 删除近期活动海报
export async function deleteActivityPoster(id: number) {
  return requestWrapper<void>('/api/merchant/wx_home/poster/delete', {
    method: 'POST',
    data: { id },
  });
}

// 近期活动海报排序
export async function sortActivityPoster(ids: number[]) {
  return requestWrapper<void>('/api/merchant/wx_home/poster/sort', {
    method: 'POST',
    data: {
      ids,
    },
  });
}
